import { useTranslation } from "react-i18next";
import { Award, CheckCircle2, Clock, Target, WifiOff } from "lucide-react";

export const MissionProgress = ({ progress, checkinState, testId = "mission-progress" }) => {
  const { t } = useTranslation();
  if (!progress || !progress.missions_total) return null;
  const p = progress;
  const checkinCopy = {
    confirmed: { key: "checkin.stateConfirmed", cls: "text-[hsl(var(--success))]" },
    pending_sync: { key: "checkin.statePendingSync", cls: "text-[hsl(var(--warning))]" },
    none: { key: "checkin.stateNone", cls: "text-muted-foreground" },
  }[checkinState];

  return (
    <section className="vy-card p-5" data-testid={testId}>
      <div className="flex items-end justify-between gap-3">
        <div>
          <h3 className="font-display text-lg font-bold">{t("progress.title")}</h3>
          <p className="text-sm text-muted-foreground" data-testid={`${testId}-count`}>
            {t("progress.completed", { done: p.missions_approved, total: p.missions_total })}
          </p>
        </div>
        <p className="font-display text-3xl font-extrabold text-primary" data-testid={`${testId}-percent`}>
          {p.percent}%
        </p>
      </div>

      <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-muted">
        <div className="h-full rounded-full bg-primary transition-[width] duration-500"
          style={{ width: `${p.percent}%` }} data-testid={`${testId}-bar`} />
      </div>

      {checkinCopy && (
        <p className={`mt-3 flex items-center gap-1.5 text-xs font-bold ${checkinCopy.cls}`}
          data-testid={`${testId}-checkin-state`} data-state={checkinState}>
          {checkinState === "confirmed" ? <CheckCircle2 className="h-3.5 w-3.5" />
            : checkinState === "pending_sync" ? <WifiOff className="h-3.5 w-3.5" />
              : <Clock className="h-3.5 w-3.5" />}
          {t(checkinCopy.key)}
        </p>
      )}

      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="rounded-xl bg-muted p-3" data-testid={`${testId}-points`}>
          <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-muted-foreground">
            <Award className="h-3.5 w-3.5" />{t("progress.pointsEarned")}
          </p>
          <p className="mt-1 font-display text-2xl font-extrabold">{p.points_earned}</p>
          <p className="text-xs text-muted-foreground">
            {t("progress.missionPoints", { n: p.mission_points })} · {t("progress.attendancePoints", { n: p.attendance_points })}
          </p>
        </div>
        <div className="rounded-xl bg-muted p-3" data-testid={`${testId}-pending-points`}>
          <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-muted-foreground">
            <Clock className="h-3.5 w-3.5" />{t("progress.potential")}
          </p>
          <p className="mt-1 font-display text-2xl font-extrabold text-muted-foreground/60">
            {p.pending_points}
          </p>
          <p className="text-xs text-muted-foreground">{t("progress.potentialHint")}</p>
        </div>
      </div>

      <ul className="mt-4 grid grid-cols-2 gap-2 text-xs font-semibold sm:grid-cols-4">
        <li className="flex items-center gap-1.5" data-testid={`${testId}-approved`}>
          <CheckCircle2 className="h-3.5 w-3.5 text-[hsl(var(--success))]" />
          {p.missions_approved} {t("status.approved")}
        </li>
        <li className="flex items-center gap-1.5" data-testid={`${testId}-pending`}>
          <Clock className="h-3.5 w-3.5 text-[hsl(var(--warning))]" />
          {p.missions_pending} {t("status.pending")}
        </li>
        <li className="flex items-center gap-1.5" data-testid={`${testId}-rejected`}>
          <Clock className="h-3.5 w-3.5 text-destructive" />
          {p.missions_rejected} {t("status.rejected")}
        </li>
        <li className="flex items-center gap-1.5 text-muted-foreground" data-testid={`${testId}-todo`}>
          <Target className="h-3.5 w-3.5" />
          {p.missions_todo} {t("progress.todo")}
        </li>
      </ul>
    </section>
  );
};
